import { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import GroupsListWithFilter from "./GroupsListWithFilter";
import { QueryGroup } from "../types";

interface GroupsContentProps {
  queryHash?: string;
}

const GroupsContent = ({ queryHash }: GroupsContentProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get dates from URL params or use defaults (last 28 days)
  // This matches how GroupsDateRange stores dates in URL
  const startDate = useMemo(() => {
    const value = searchParams.get("start_date");
    return value ? new Date(value) : dayjs().subtract(28, "day").toDate();
  }, [searchParams]);

  const endDate = useMemo(() => {
    const value = searchParams.get("end_date");
    return value ? new Date(value) : dayjs().toDate();
  }, [searchParams]);

  // Construct row URL with current date params (always includes dates)
  const getRowUrl = useCallback(
    (group: QueryGroup) => {
      return `/query/groups/${group.query_hash}?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`;
    },
    [startDate, endDate]
  );

  const getRowState = useCallback((group: QueryGroup) => ({ entityName: group.query_hash }), []);

  // Handle navigation when a specific queryHash is found
  const handleGroupFound = useCallback(
    (group: QueryGroup) => {
      navigate(getRowUrl(group));
    },
    [navigate, getRowUrl]
  );

  return (
    <GroupsListWithFilter
      getRowUrl={getRowUrl}
      getRowState={getRowState}
      onGroupFound={handleGroupFound}
      queryHash={queryHash}
    />
  );
};

export default GroupsContent;
